import angleSmallDownSvg from "@icons/angle-small-down.svg?url";
import CheckCircleGreen from "@icons/check-circle-green.svg?react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import React from "react";
import { useForm } from "react-hook-form";
import invariant from "tiny-invariant";

import { ActionGuard, useGuard } from "@/common/acl/action-guard/action-guard";
import { Button } from "@/common/components/button/button";
import { PageTitle } from "@/common/components/page-title/page-title";
import {
  PendingUserBanner,
  useIsPendingUser,
} from "@/common/components/pending-user-banner/pending-user-banner";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useCompany } from "@/common/providers/company-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { currencyFormatter, getPrice } from "@/common/services/formatter";
import { getLocalizedPeriods } from "@/common/utils/period";
import { getCompanyPlans, PlanPeriodTo, PlanTo } from "@/generated/api/users";
import { getTranslation } from "@/generated/plan-feature-i18n";
import { TwoColumnLayout } from "@/routes/layout/layout";

import { companyQuery } from "../../api/company.api";

const Plans = ({ children }: React.PropsWithChildren) => {
  const { company } = useCompany();
  invariant(company);
  const plans = useSuspenseQuery(plansQuery({ companyId: company.id }));
  const isPending = useIsPendingUser();

  return (
    <TwoColumnLayout
      left={{
        content: (
          <div className="@container flex w-full max-w-full flex-col items-center gap-y-6">
            {isPending ? (
              <PendingUserBanner.Banner>
                <PendingUserBanner.Message />
              </PendingUserBanner.Banner>
            ) : null}
            {company.plan.type === "BASIC" ? (
              <p>
                <Trans>
                  Využíváte základní předplatné {company.plan.type}, proto jsou
                  pro vás některé funkce nedostupné. Vyberte si vyšší balíček
                  služeb.
                </Trans>
              </p>
            ) : null}
            <div className="mx-auto grid w-full max-w-[1038px] grid-cols-1 justify-items-center gap-6 gap-y-6 @[1038px]:grid-cols-3">
              {plans.data.map((plan) => (
                <Plan
                  active={plan.id === company.plan.id}
                  key={plan.id}
                  plan={plan}
                />
              ))}
            </div>
            <Spacer className="h-6" />
          </div>
        ),
        header: (
          <>
            {children}
            <div className="mx-auto">
              <PageTitle>
                <Trans>Odemkněte si všechny funkce</Trans>
              </PageTitle>
            </div>
          </>
        ),
      }}
      right={false}
    ></TwoColumnLayout>
  );
};

const Plan = ({ active, plan }: { active: boolean; plan: PlanTo }) => {
  const i18n = useLingui();
  return (
    <section
      className={`shadow-can-light-box relative flex w-full flex-col items-center gap-y-2 self-start rounded-2xl p-6 @sm:max-w-[342px] @sm:min-w-[330px] ${active ? "bg-can-silver-gray" : ""}`}
    >
      <h2 className="text-can-midnight-steel text-center text-xl font-bold uppercase">
        {plan.name}
      </h2>
      <p>
        {active ? (
          <Trans>Toto je váš aktuální balíček služeb.</Trans>
        ) : (
          <Trans>V balíčku najdete tyto služby:</Trans>
        )}
      </p>
      <ul className="flex w-full flex-col gap-y-6">
        {plan.features.map((feature) => {
          const featureDescriptionId = `${feature.feature.type.toLowerCase()}_${feature.mode.toLowerCase()}_description`;
          const featureTitleId = `${feature.feature.type.toLowerCase()}_${feature.mode.toLowerCase()}_title`;
          return (
            <li className="flex gap-x-4" key={feature.feature.type}>
              <span className="inline-block shrink-0 self-start rounded-full bg-[#ebebeb] p-4">
                <img className="h-6 w-6" src={feature.feature.icon} />
              </span>
              <div className="flex flex-col gap-1">
                <h3 className="text-can-midnight-steel font-bold">
                  {i18n._(getTranslation(featureTitleId))}
                </h3>
                <p className="leading-4">
                  <small>{i18n._(getTranslation(featureDescriptionId))}</small>
                  <br />
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {plan.periods.length > 0 ? (
        <Pricing periods={plan.periods} plan={plan.type} />
      ) : (
        <FreePricing />
      )}
      {active ? <CheckCircleGreen className="absolute top-4 right-4" /> : null}
    </section>
  );
};

const FreePricing = () => (
  <span className="bg-can-can-slate-blue-gray mt-6 inline-block w-full rounded-2xl bg-[#ebebeb] p-3 text-center">
    <Trans>Měsíční předplatné zdarma</Trans>
  </span>
);

const Pricing = ({
  plan,
  periods,
}: {
  plan: string;
  periods: PlanPeriodTo[];
}) => {
  const form = useForm<{ period: PlanPeriodTo["type"] }>({
    defaultValues: {
      period: "YEARLY",
    },
  });
  const activeSubscription = useActiveSubscriptionEndDate();
  const translations = getLocalizedPeriods();
  const activePeriod = form.watch("period");
  const activePrice = periods.find((period) => period.type === activePeriod);
  invariant(activePrice);
  const formattedPrice = getFormattedPrice(
    getPrice(activePrice.price, activePrice.currency.precision),
    activePrice.currency.type,
  );
  const { actions } = useSidebar();

  const handlePeriodClick = () => {
    const periodId = activePrice.id;
    invariant(periodId);
    actions.openSubscriptionOverview({
      period: activePeriod,
      periodId,
      plan,
      price: formattedPrice,
    });
  };

  const handleGuardedPeriodClick = useGuard({
    action: handlePeriodClick,
  });

  return (
    <div className="mt-6 flex w-full flex-col">
      <h4>
        <Trans>Období</Trans>
      </h4>
      <Spacer className="h-4" />
      <select
        className="text-can-midnight-steel group-has-[[role=alert]]:border-can-russet h-[calc(--spacing(14)+2px)] w-full rounded-lg p-4 font-bold"
        defaultValue=""
        style={{ backgroundImage: `url(${angleSmallDownSvg})` }}
        {...form.register("period", {
          required: t`Vyberte prosím období`,
        })}
      >
        {periods.map((period) => (
          <option key={period.type} value={period.type}>
            {translations[period.type]}
          </option>
        ))}
      </select>
      <Spacer className="h-6" />
      <Button onClick={handleGuardedPeriodClick} variant="primary">
        <Trans>
          Přejít na {plan} za {formattedPrice}
        </Trans>
      </Button>
      <Spacer className="h-2" />
      <small className="text-center text-xs">
        <Trans>Cena je uvedena bez DPH.</Trans>
      </small>
      {activeSubscription?.planType === plan ? (
        <>
          <Spacer className="h-2" />
          <small className="text-center text-xs">
            <strong>
              <Trans>
                Vaše současné předplatné skončí {activeSubscription.expiresAt}.
              </Trans>
            </strong>
          </small>
        </>
      ) : null}
    </div>
  );
};

const getFormattedPrice = (price: number, currency: string) => {
  return currencyFormatter(currency).format(price);
};

const plansQuery = ({ companyId }: { companyId: number }) =>
  queryOptions({
    queryFn: ({ signal }) =>
      getCompanyPlans(companyId, { inPlan: true }, { signal }),
    queryKey: ["plans", companyId],
    staleTime: Infinity,
  });

const LockedScreen = ({ children }: React.PropsWithChildren) => (
  <ActionGuard
    grant="PAYMENT_CREATE"
    guard={{
      action: <Trans>Zavřít</Trans>,
      content: <Trans>Nemáte dostatečné oprávnění.</Trans>,
      title: <Trans>Akci nelze dokončit</Trans>,
    }}
  >
    <React.Suspense
      fallback={
        <div className="mx-auto pt-14">
          <Spinner />
        </div>
      }
    >
      <Plans>{children}</Plans>
    </React.Suspense>
  </ActionGuard>
);

const useActiveSubscriptionEndDate = () => {
  const { company: activeCompany } = useCompany();
  invariant(activeCompany);
  const company = useSuspenseQuery(companyQuery(activeCompany.id));
  const manualSubscriptions = company.data.subscriptions.filter(
    (s) => s.source === "MANUAL",
  );
  const paidSubscriptions = company.data.subscriptions.filter(
    (s) => s.source === "PAID",
  );
  const trialSubscriptions = company.data.subscriptions.filter(
    (s) => s.source === "TRIAL",
  );

  if (manualSubscriptions.length === 0 && paidSubscriptions.length === 0) {
    return;
  }

  const subscription =
    manualSubscriptions.at(0) ??
    trialSubscriptions.at(0) ??
    paidSubscriptions.at(0);

  if (subscription?.expiresAt) {
    return {
      expiresAt: DateTime.fromISO(subscription.expiresAt).toLocaleString(
        DateTime.DATE_MED,
      ),
      planType: subscription.plan.type,
    };
  }
};

export { LockedScreen };
